import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { color, gutter } from '../../styles/mixins';
import { p14 } from '../../styles/typography';

const Host = styled.div`
  position: relative;
  max-width: 500px;
  width: 100%;
  display: block;
  min-height: 54px;
  padding-top: 14px;
`;

const Input = styled.input`
  pointer-events: none;
  position: absolute;
  height: 0;
  width: 100%;
  outline: none;

  &::-webkit-slider-thumb {
    background-color: ${color('white')};
    box-shadow: 0 0 0 2px ${color('blue60')};
    border: none;
    border-radius: 50%;
    cursor: pointer;
    height: ${gutter(3)};
    width: ${gutter(3)};
    pointer-events: all;
    position: relative;
    -webkit-appearance: none;
    -webkit-tap-highlight-color: transparent;
  }

  &::-moz-range-thumb {
    background-color: ${color('white')};
    box-shadow: 0 0 0 2px ${color('blue60')};
    border: none;
    border-radius: 50%;
    cursor: pointer;
    height: ${gutter(3)};
    width: ${gutter(3)};
    pointer-events: all;
    position: relative;
    -webkit-appearance: none;
    -webkit-tap-highlight-color: transparent;
  }

  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
`;

const MinInput = styled(Input)<{ indexTop: boolean }>`
  z-index: ${(props) => (props.indexTop ? 5 : 3)};
`;

const MaxInput = styled(Input)`
  z-index: 4;
`;

const Slider = styled.div`
  position: relative;
  width: 100%;
`;

const SliderTrack = styled.div`
  position: absolute;
  border-radius: ${(props) => props.theme.border.radius}px;
  height: 2px;
  background-color: ${color('gray40')};
  width: 100%;
  z-index: 1;
`;

const SliderRange = styled.div`
  position: absolute;
  border-radius: ${(props) => props.theme.border.radius}px;
  height: 2px;
  background-color: ${color('blue60')};
  z-index: 2;
`;

const SliderLeftValue = styled.div`
  ${p14};
  position: absolute;
  margin-top: 20px;
  left: 6px;
`;

const SliderRightValue = styled.div`
  ${p14};
  position: absolute;
  margin-top: 20px;
  right: -4px;
`;

interface RangeProps {
  min: number;
  max: number;
  minValue?: number;
  maxValue?: number;
  onChange: ({ min, max }: { min: number; max: number }) => void;
}

export const Range = ({
  min,
  max,
  minValue,
  maxValue,
  onChange,
}: RangeProps) => {
  const [minVal, setMinVal] = useState(min);
  const [maxVal, setMaxVal] = useState(max);
  const minValRef = useRef<HTMLInputElement>(null);
  const maxValRef = useRef<HTMLInputElement>(null);
  const range = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof minValue !== 'undefined') {
      setMinVal(minValue);
    }

    if (typeof maxValue !== 'undefined') {
      setMaxVal(maxValue);
    }
  }, [minValue, maxValue]);

  // Convert to percentage
  const getPercent = useCallback(
    (value: number) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  // Set width of the range to decrease from the left side
  useEffect(() => {
    if (maxValRef.current) {
      const minPercent = getPercent(minVal);
      const maxPercent = getPercent(+maxValRef.current.value); // Precede with '+' to convert the value from type string to type number

      if (range.current) {
        range.current.style.left = `${minPercent}%`;
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [minVal, getPercent]);

  // Set width of the range to decrease from the right side
  useEffect(() => {
    if (minValRef.current) {
      const minPercent = getPercent(+minValRef.current.value);
      const maxPercent = getPercent(maxVal);

      if (range.current) {
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [maxVal, getPercent]);

  // Get min and max values when their state changes
  useEffect(() => {
    onChange({ min: minVal, max: maxVal });
  }, [minVal, maxVal, onChange]);

  return (
    <Host>
      <label htmlFor="min range">
        <MinInput
          id="min"
          type="range"
          min={min}
          max={max}
          value={minVal}
          ref={minValRef}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const value = Math.min(+event.target.value, maxVal - 1);
            setMinVal(value);
            event.target.value = value.toString();
          }}
          indexTop={minVal > max - 100}
        />
      </label>
      <label htmlFor="max range">
        <MaxInput
          id="max"
          type="range"
          min={min}
          max={max}
          value={maxVal}
          ref={maxValRef}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const value = Math.max(+event.target.value, minVal + 1);
            setMaxVal(value);
            event.target.value = value.toString();
          }}
        />
      </label>

      <Slider>
        <SliderTrack />
        <SliderRange ref={range} />
        <SliderLeftValue>{minVal} USD</SliderLeftValue>
        <SliderRightValue>{maxVal} USD</SliderRightValue>
      </Slider>
    </Host>
  );
};
